import Form, { Field as BaseField } from "rc-field-form";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import Button from "../../../components/Button";
import Field from "../../../components/Field";
import DateInput from "../../../components/Inputs/DateInput";
import DurationInput, { getDurationInputValue } from "../../../components/Inputs/DurationInput";
import WeekdaySelect from "../../../components/Inputs/WeekdaySelect";
import NumberInput from "../../../components/Inputs/NumberInput";
import { generateErrorsConfigForForm } from "../../../utils/helpers/errors";
import { renderDurationAsFormat } from "../../../utils/helpers/date";
import { useCompanyTimeFormat } from "../../../utils/hooks/company";

function ExtendedDurationInput({ value: _value, onChange: _onChange, timeFormat, ...props }) {
	const companyTimeFormat = useCompanyTimeFormat();
	const format = timeFormat ?? companyTimeFormat ?? "HH:mm";
	const value = useMemo(() => renderDurationAsFormat(_value || 0, format), [_value, format]);
	const onChange = useCallback(
		(val) => {
			if (_onChange) _onChange(getDurationInputValue(val));
		},
		[_onChange],
	);
	return <DurationInput value={value} onChange={onChange} {...props} />;
}

function ExpectedHoursForm({ mode, close, values, error, loading, create, update }) {
	const { t } = useTranslation();
	const [form] = Form.useForm();

	const onFinish = useCallback(
		(data) => {
			const payload = {
				startEffectiveDate: moment(data.startEffectiveDate).format("YYYY-MM-DD"),
				endEffectiveDate: data.endEffectiveDate
					? moment(data.endEffectiveDate).format("YYYY-MM-DD")
					: null,
				expectedHours: data.expectedHours != null ? Number(data.expectedHours) : null,
				expectedWorkingDays:
					Array.isArray(data.expectedWorkingDays) && data.expectedWorkingDays.length > 0
						? data.expectedWorkingDays.map((d) =>
							typeof d === "string" && d.charAt(0) === d.charAt(0).toLowerCase()
								? d.charAt(0).toUpperCase() + d.slice(1).toLowerCase()
								: d,
						)
						: null,
				expectedWorkingDaysCount:
					data.expectedWorkingDaysCount != null
						? Number(data.expectedWorkingDaysCount)
						: Array.isArray(data.expectedWorkingDays)
							? data.expectedWorkingDays.length
							: null,
			};
			if (mode === "edit" && values?.id) {
				update(values.id, payload, close);
			} else {
				create(payload, close);
			}
		},
		[mode, values, create, update, close],
	);

	useEffect(() => {
		form.resetFields();
		form.setFieldsValue({
			startEffectiveDate: values?.startEffectiveDate
				? moment(values.startEffectiveDate).toDate()
				: undefined,
			endEffectiveDate: values?.endEffectiveDate
				? moment(values.endEffectiveDate).toDate()
				: undefined,
			expectedHours: values?.expectedHours ?? undefined,
			expectedWorkingDays: values?.expectedWorkingDays?.length
				? values.expectedWorkingDays.map((d) =>
					typeof d === "string" ? d.charAt(0).toLowerCase() + d.slice(1).toLowerCase() : d,
				)
				: [],
			expectedWorkingDaysCount: values?.expectedWorkingDaysCount ?? undefined,
		});
	}, [form, values, mode]);

	useEffect(() => {
		const fieldErrors = generateErrorsConfigForForm(
			[
				"startEffectiveDate",
				"endEffectiveDate",
				"expectedHours",
				"expectedWorkingDays",
				"expectedWorkingDaysCount",
			],
			error,
		);
		form.setFields(fieldErrors);
	}, [form, error]);

	return (
		<Form form={form} layout="vertical" onFinish={onFinish} scrollToFirstError>
			<Field
				name="startEffectiveDate"
				label={t("start-effective-date")}
				rules={[{ required: true, message: t("this-field-is-required") }]}
			>
				<DateInput placeholderText={t("start-date")} />
			</Field>

			<BaseField shouldUpdate>
				{(_, __, { getFieldValue }) => (
					<Field
						name="endEffectiveDate"
						label={t("end-effective-date")}
						rules={[
							{
								validator: (_, value) => {
									const startEffectiveDate = getFieldValue("startEffectiveDate");
									if (!value || !startEffectiveDate) return Promise.resolve();
									if (moment(value).isBefore(moment(startEffectiveDate), "date")) {
										return Promise.reject(new Error(t("end-date-must-be-after-start-date")));
									}
									return Promise.resolve();
								},
							},
						]}
					>
						<DateInput placeholderText={t("end-date")} />
					</Field>
				)}
			</BaseField>

			<Field
				name="expectedHours"
				label={t("expected-hours")}
				rules={[
					{
						validator: (_, value) => {
							if (value == null || value === "") return Promise.resolve();
							const n = Number(value);
							if (Number.isNaN(n) || n < 0)
								return Promise.reject(new Error(t("expected-hours-must-be-greater-than-or-equal-to-zero")));
							return Promise.resolve();
						},
					},
				]}
			>
				<ExtendedDurationInput />
			</Field>

			<Field name="expectedWorkingDays" label={t("expected-working-days")}>
				<WeekdaySelect mode="multiple" />
			</Field>

			<Field
				name="expectedWorkingDaysCount"
				label={t("expected-working-days-count")}
				info={t("working-days-count-during-the-pay-period-of-the-user")}
				rules={[
					{
						validator: (_, value) => {
							if (value == null || value === "") return Promise.resolve();
							const n = Number(value);
							if (Number.isNaN(n) || n < 0)
								return Promise.reject(new Error(t("expected-working-days-count-must-be-greater-than-or-equal-to-zero")));
							return Promise.resolve();
						},
					},
				]}
			>
				<NumberInput min={0} placeholder={t("expected-working-days-count")} />
			</Field>

			<div className="d-flex justify-content-end">
				<Button
					className="btn-round btn-icon shadow-none border btn btn-secondary btn-sm"
					onClick={close}
					disabled={loading}
				>
					{t("cancel")}
				</Button>
				<Button
					color="primary"
					type="submit"
					loading={loading}
					className="btn-round btn-icon shadow-none border px-3 btn btn-dark btn-sm"
				>
					{t("save")}
				</Button>
			</div>
		</Form>
	);
}

export default ExpectedHoursForm;
